import { Injectable } from '@angular/core';
import { Server } from 'miragejs';
import { environment } from 'src/environments/environment';
import {
  ApiAuthenticationService,
  ApiBookingsService,
  ApiFaqService,
  ApiNotificationsService,
  ApiOrdersService,
  ApiPaymentsService,
  ApiRegistrationService,
  ApiRestaurantsService,
  ApiSurveysService,
  ApiTicketsService,
  RegisterRequest,
} from '../api';
import { MOCK_ACCOUNT_BALANCE, MOCK_RELOAD_ACCOUNT_BALANCE_CONFIG } from './mock-account-balance';
import { MOCK_ANIMATIONS } from './mock-animations';
import {
  MOCK_CHANGE_PASSWORD,
  MOCK_GET_LIKES,
  MOCK_LOGIN,
  MOCK_LOGIN_SSO,
  MOCK_REFRESH_TOKEN,
  MOCK_UPDATE_ACCOUNT_EMAIL,
  MOCK_UPDATE_ACCOUNT_INFOS,
  MOCK_UPDATE_BALANCE_ALERT,
  MOCK_UPDATE_LIKE_NOTIFICATION,
  MOCK_UPDATE_PREFERRED_LANGUAGE_INFOS,
  MOCK_USER_INFO,
} from './mock-auth';
import { MOCK_BOOKINGS, MOCK_DELETE_BOOKING, MOCK_LIEUX, MOCK_SAVE_PRESENCE, MOCK_SLOTS } from './mock-booking';
import { MOCK_FAQ } from './mock-faq';
import { MOCK_CHECK_CART, MOCK_CREATE_ORDER, MOCK_ORDER, MOCK_ORDERS, MOCK_SCORP_ORDERS, MOCK_TICKETS } from './mock-history';
import {
  MOCK_DELETE_NOTIFICATION,
  MOCK_GET_NOTIFICATION_COUNT,
  MOCK_GET_NOTIFICATION_SETTINGS,
  MOCK_NOTIFICATIONS,
  MOCK_NOTIFICATION_AS_READ,
  MOCK_UPDATE_NOTIFICATION_SETTINGS,
  MOCK_UPDATE_OPTIN_SETTINGS,
} from './mock-notifications';
import {
  MOCK_CONFIRM_EMAIL_SENT,
  MOCK_EMAIL_CONFIRMED,
  MOCK_REGISTER,
  MOCK_REGISTRATION_ENSURE_EMAIL_VALIDITY,
  MOCK_REGISTRATION_SITE,
  MOCK_REGISTRATION_SITES,
  UNREGISTER,
} from './mock-registration';
import {
  MOCK_GET_CURRENT_FLUIDITE,
  MOCK_GET_EXTERNAL_RESTAURANT_HOME,
  MOCK_GET_RESTAURANTS,
  MOCK_GET_RESTAURANT_FLUIDITE,
  MOCK_GET_RESTAURANT_MENU,
  MOCK_SCORP_ANTI_GASPI_RESTAURANT_ID,
  MOCK_SCORP_RESTAURANT_ID,
  MOCK_UPDATE_DISH_LIKES,
} from './mock-restaurants';
import { MOCK_GET_CHAT, MOCK_GET_CHATS, MOCK_GET_SURVEY, MOCK_REPLY_TO_SURVEY } from './mock-surveys';

@Injectable({
  providedIn: 'root',
})
export class DemoProxyService {
  private server?: Server;
  url = '';
  constructor() {
    this.url = `${environment.apiUrl}/`;
  }

  enable() {
    if (this.server) return;

    const url = this.url;

    this.server = new Server({
      routes() {
        // Careful with 'this' keyword here, routes() is a function
        this.pretender.get('data:image/*', this.pretender.passthrough);

        this.passthrough();
        this.timing = 800;

        this.urlPrefix = url;

        /** AUTH */
        this.post(ApiAuthenticationService.PostAuthenticationLoginPath, () => MOCK_LOGIN.OK());
        this.post(ApiAuthenticationService.PostAuthenticationRefreshPath, () => MOCK_REFRESH_TOKEN.OK());
        this.get(ApiAuthenticationService.GetAuthenticationUserInfoPath, () => MOCK_USER_INFO.OK);
        this.get(ApiAuthenticationService.GetAuthenticationUserLikesPath, () => MOCK_GET_LIKES.OK);
        this.post(ApiAuthenticationService.PostAuthenticationLoginSsoPath, () => MOCK_LOGIN_SSO.OK);
        this.patch(ApiAuthenticationService.PatchAuthenticationAccountInfosPath, () => MOCK_UPDATE_ACCOUNT_INFOS.OK);
        this.put(ApiAuthenticationService.PutAuthenticationLanguagePath, () => MOCK_UPDATE_PREFERRED_LANGUAGE_INFOS.OK);
        this.post(ApiAuthenticationService.PostAuthenticationChangePasswordPath, () => MOCK_CHANGE_PASSWORD.OK);
        this.patch(ApiAuthenticationService.PatchAuthenticationAccountEmailPath, () => MOCK_UPDATE_ACCOUNT_EMAIL.OK);
        this.post(ApiAuthenticationService.PostAuthenticationBalanceAlertPath, () => MOCK_UPDATE_BALANCE_ALERT.OK);
        this.patch('/Authentication/likes/:DishId/update-notification', () => MOCK_UPDATE_LIKE_NOTIFICATION.OK);

        /** FAQ */
        this.get(ApiFaqService.GetFaqPath, () => MOCK_FAQ.OK);

        /** HISTORY  */

        this.get(ApiTicketsService.GetTicketsPath, (s, r) => {
          switch (r.queryParams?.PageIndex) {
            case '0': {
              return MOCK_TICKETS.OK_PAGE0;
            }
            case '1': {
              return MOCK_TICKETS.OK_PAGE1;
            }
            default: {
              return MOCK_TICKETS.KO;
            }
          }
        });
        this.get(ApiOrdersService.GetOrdersPath, (s, r) => {
          if (r.queryParams?.RestaurantWithExternalOrderHistoryId) {
            return MOCK_ORDERS.EXTERNAL;
          }

          switch (r.queryParams?.PageIndex) {
            case '0': {
              return MOCK_ORDERS.OK_PAGE0;
            }
            case '1': {
              return MOCK_ORDERS.OK_PAGE1;
            }
            default: {
              return MOCK_ORDERS.KO;
            }
          }
        });

        this.get(ApiOrdersService.GetOrdersOrderssummaryPath, (_s, _r) => {
          return MOCK_SCORP_ORDERS.OK;
        });

        this.get(handleRouteParams(ApiOrdersService.GetOrdersByOrderidPath), (_s, _r) => {
          return MOCK_ORDER.OK;
        });

        this.post(ApiOrdersService.PostOrdersCheckcartPath, (_s, r) => {
          switch (JSON.parse(r.requestBody).restaurantId) {
            case MOCK_SCORP_ANTI_GASPI_RESTAURANT_ID:
              return MOCK_CHECK_CART.OK_ANTIGASPI;
            default:
              return MOCK_CHECK_CART.OK;
          }
        });
        this.post(ApiOrdersService.PostOrdersPath, (_s, _r) => MOCK_CREATE_ORDER.OK);

        /**NOTIFICATIONS */
        this.get(ApiNotificationsService.GetNotificationsPath, (s, r) => {
          switch (r.queryParams?.PageIndex) {
            case '0': {
              return MOCK_NOTIFICATIONS.OK_PAGE0;
            }
            case '1': {
              return MOCK_NOTIFICATIONS.OK_PAGE1;
            }
            default: {
              return MOCK_NOTIFICATIONS.KO;
            }
          }
        });
        this.delete(ApiNotificationsService.DeleteNotificationsNotificationPath, () => MOCK_DELETE_NOTIFICATION.OK);
        this.get(ApiNotificationsService.GetNotificationsSettingsPath, () => MOCK_GET_NOTIFICATION_SETTINGS.OK);
        this.post(ApiNotificationsService.PostNotificationsSettingsPath, () => MOCK_UPDATE_NOTIFICATION_SETTINGS.OK);
        this.post(ApiNotificationsService.PostNotificationsOptInSettingsPath, () => MOCK_UPDATE_OPTIN_SETTINGS.OK);
        this.post(ApiNotificationsService.PostNotificationsSetAsReadPath, () => MOCK_NOTIFICATION_AS_READ.OK);
        this.get(ApiNotificationsService.GetNotificationsUnreadCountPath, () => MOCK_GET_NOTIFICATION_COUNT.OK);

        /** BOOKINGS */
        this.get(ApiBookingsService.GetBookingsPath, (s, r) => {
          switch (r.queryParams?.PageIndex) {
            case '0': {
              return MOCK_BOOKINGS.OK_PAGE0;
            }
            case '1': {
              return MOCK_BOOKINGS.OK_PAGE1;
            }
            default: {
              return MOCK_BOOKINGS.KO;
            }
          }
        });
        this.get(ApiBookingsService.GetBookingsLieuxPath, () => MOCK_LIEUX.OK_LIEUX);
        this.get(ApiBookingsService.GetBookingsSlotsPath, () => MOCK_SLOTS.OK);
        this.post(ApiBookingsService.PostBookingsSaveBookingPath, (s, r) => MOCK_SAVE_PRESENCE.OK(JSON.parse(r.requestBody)));
        this.delete(ApiBookingsService.DeleteBookingsBookingPath, () => MOCK_DELETE_BOOKING.OK);

        /** PAYMENTS */
        this.get(ApiPaymentsService.GetPaymentsAccountBalancePath, () => MOCK_ACCOUNT_BALANCE.OK);
        this.get(ApiPaymentsService.GetPaymentsReloadAccountBalanceConfigPath, () => MOCK_RELOAD_ACCOUNT_BALANCE_CONFIG.OK);
        // this.post(ApiPaymentsService.PostPaymentsReloadAccountBalancePath, () => MOCK_RELOAD_ACCOUNT_BALANCE.KO); // No OK response for this request

        /** REGISTRATION */
        this.get(ApiRegistrationService.GetRegistrationSitePath, () => MOCK_REGISTRATION_SITE.OK);
        this.get(ApiRegistrationService.GetRegistrationSitesPath, () => MOCK_REGISTRATION_SITES.OK);
        this.post(ApiRegistrationService.PostRegistrationEnsureEmailValidityPath, () => MOCK_REGISTRATION_ENSURE_EMAIL_VALIDITY.OK);
        this.post(ApiRegistrationService.PostRegistrationRegisterPath, (s, r) => {
          const body: RegisterRequest = JSON.parse(r.requestBody);
          if (body.enforceBadgeNumber) return MOCK_REGISTER.OK;

          return MOCK_REGISTER.AMBIGOUS_ACCOUNTS;
        });

        /** REGISTRATION > EMAIL CONFIRMATION */
        this.post(ApiRegistrationService.PostRegistrationSendConfirmEmailPath, () => MOCK_CONFIRM_EMAIL_SENT.OK);
        this.post(ApiRegistrationService.PostRegistrationConfirmEmailPath, () => MOCK_EMAIL_CONFIRMED.OK);

        /** UNREGISTER */
        this.post(ApiRegistrationService.PostRegistrationUnregisterPath, () => UNREGISTER.OK);

        /** RESTAURANTS */
        this.get(ApiRestaurantsService.GetRestaurantsPath, (s, r) => {
          return r.requestHeaders['Accept-Language'] == 'en' ? MOCK_GET_RESTAURANTS.OK_MULTI_ENG : MOCK_GET_RESTAURANTS.OK_MULTI;
        });

        this.get('/Restaurants/:RestaurantId/home-redirection', (s, r) => {
          switch (r.params.RestaurantId) {
            case '8': {
              return MOCK_GET_EXTERNAL_RESTAURANT_HOME.EMBEDDED_NAV_BOTTOM;
            }
            case '9': {
              return MOCK_GET_EXTERNAL_RESTAURANT_HOME.EMBEDDED_NAV_TOP;
            }
            case '10': {
              return MOCK_GET_EXTERNAL_RESTAURANT_HOME.NOT_EMBEDDED;
            }
            case '11': {
              return MOCK_GET_EXTERNAL_RESTAURANT_HOME.KO;
            }
            case '14': {
              return MOCK_GET_EXTERNAL_RESTAURANT_HOME.EXTERNAL_SAT;
            }
            default: {
              return MOCK_GET_EXTERNAL_RESTAURANT_HOME.EMBEDDED_NAV_BOTTOM;
            }
          }
        });
        this.get('/Restaurants/:RestaurantId/menus', (s, r) => {
          switch (r.params.RestaurantId) {
            case '2': {
              return MOCK_GET_RESTAURANT_MENU.OK_WITHOUT_NUTRISCORE;
            }
            case '4': {
              return MOCK_GET_RESTAURANT_MENU.EMPTY;
            }
            case '6': {
              return MOCK_GET_RESTAURANT_MENU.OK_ALL_CLOSED;
            }
            case '7': {
              return MOCK_GET_RESTAURANT_MENU.OK_WITH_CLOSURE;
            }
            case MOCK_SCORP_RESTAURANT_ID + '': {
              return MOCK_GET_RESTAURANT_MENU.OK(true, true);
            }
            case MOCK_SCORP_ANTI_GASPI_RESTAURANT_ID + '': {
              return MOCK_GET_RESTAURANT_MENU.OK_ANTIGASPI();
            }
            default: {
              return MOCK_GET_RESTAURANT_MENU.OK(false, false);
            }
          }
        });

        this.get('/Restaurants/:RestaurantId/fluiditeRates', (s, r) => {
          switch (r.params.RestaurantId) {
            case '1':
              return MOCK_GET_RESTAURANT_FLUIDITE.OK_WITH_CURRENT_LOWER;
            case '3':
              return MOCK_GET_RESTAURANT_FLUIDITE.KO_RESTAURANT_CLOSED_LAST_WEEK;
            case '4':
              return MOCK_GET_RESTAURANT_FLUIDITE.KO;
            default:
              return MOCK_GET_RESTAURANT_FLUIDITE.OK_WITH_CURRENT_HIGHER;
          }
        });

        this.get('/Restaurants/:RestaurantId/currentFluidite', (s, r) => {
          switch (+r.params.RestaurantId % 3) {
            case 0:
              return MOCK_GET_CURRENT_FLUIDITE.OK_Faible;
            case 1:
              return MOCK_GET_CURRENT_FLUIDITE.OK_Moyen;
            case 2:
              return MOCK_GET_CURRENT_FLUIDITE.OK_Eleve;
            default:
              return MOCK_GET_CURRENT_FLUIDITE.OK_Faible;
          }
        });

        this.get(ApiRestaurantsService.GetRestaurantsAnimationsPath, (s, r) => {
          switch (r.queryParams?.PageIndex) {
            case '0': {
              return r.requestHeaders['Accept-Language'] == 'en' ? MOCK_ANIMATIONS.OK_PAGE0_ENG : MOCK_ANIMATIONS.OK_PAGE0_FR;
            }
            case '1': {
              return MOCK_ANIMATIONS.OK_PAGE1;
            }
            default: {
              return MOCK_ANIMATIONS.KO;
            }
          }
        });

        this.patch('/Restaurants/dish/:DishId/likes', () => MOCK_UPDATE_DISH_LIKES.OK);

        /** SURVEYS */
        this.get('/Surveys/:id', (s, r) => MOCK_GET_SURVEY.OK(r.params?.id === '2'));
        this.post(ApiSurveysService.PostSurveysReplyPath, () => MOCK_REPLY_TO_SURVEY.OK);
        this.get('/Surveys/chats/:ResponseId', () => MOCK_GET_CHAT.OK);
        this.get(ApiSurveysService.GetSurveysChatsPath, (s, r) => {
          switch (r.queryParams?.PageIndex) {
            case '0': {
              return MOCK_GET_CHATS.OK_PAGE0;
            }
            case '1': {
              return MOCK_GET_CHATS.OK_PAGE1;
            }
            default: {
              return MOCK_ANIMATIONS.KO;
            }
          }
        });

        this.passthrough();
      },
    });
  }

  disable() {
    if (!this.server) return;

    this.server.shutdown();
    this.server = undefined;
  }
}

export const handleRouteParams = (str: string) => str.replace('{', ':').replace('}', '');
